import type { NativeCursor, NativeRequestEvent, NativeRequests } from './types';

export type NativePageCursor = { request: NativeCursor | null; event: NativeCursor | null };

export function nextNativePage(page: NativeRequests, _pages?: NativeRequests[], previous?: NativePageCursor | null): NativePageCursor | undefined {
  return page.next_request_cursor || page.next_event_cursor
    ? { request: page.next_request_cursor ?? previous?.request ?? null, event: page.next_event_cursor ?? previous?.event ?? null }
    : undefined;
}

export function nativePagePath(cursor: NativePageCursor | null): string {
  const params = new URLSearchParams();
  if (cursor?.request) {
    params.set('request_before_at', cursor.request.at);
    params.set('request_before_id', cursor.request.id);
  }
  if (cursor?.event) {
    params.set('before_at', cursor.event.at);
    params.set('before_id', cursor.event.id);
  }
  return `/me/support/native-requests${params.size ? `?${params}` : ''}`;
}

/** Separate cursors can repeat the other stream; first (freshest) copy wins. */
export function mergeNativePages(pages: NativeRequests[]): NativeRequests {
  const requests = new Map<string, NativeRequests['requests'][number]>();
  const events = new Map<string, NativeRequestEvent>();
  for (const page of pages) {
    for (const request of page.requests) if (!requests.has(request.id)) requests.set(request.id, request);
    for (const event of page.events) if (!events.has(event.id)) events.set(event.id, event);
  }
  const last = pages.at(-1);
  return {
    requests: [...requests.values()], events: [...events.values()],
    requests_complete: last?.requests_complete ?? true,
    has_more_events: last?.has_more_events ?? false,
    next_request_cursor: last?.next_request_cursor ?? null,
    next_event_cursor: last?.next_event_cursor ?? null,
  };
}

export function requestedIdFromSearch(search: string): string | null {
  const values = new URLSearchParams(search).getAll('support_request');
  if (values.length !== 1 || !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(values[0])) return null;
  const id = values[0].toLowerCase();
  return id;
}

export function requestSelectionFromSearch(search: string, enabled: boolean, ownedIds: ReadonlySet<string>): string | null {
  const id = enabled ? requestedIdFromSearch(search) : null;
  return id && ownedIds.has(id) ? id : null;
}

export function compareTimeline(a: { id: string; at: string; event?: boolean }, b: { id: string; at: string; event?: boolean }): number {
  const time = Date.parse(a.at) - Date.parse(b.at);
  // At equal instants, the message precedes the resulting status line.
  return time || Number(Boolean(a.event)) - Number(Boolean(b.event)) || a.id.localeCompare(b.id);
}
